import React, { useCallback, useState } from 'react';
import { Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { reorder } from 'utils';
import ChecklistSectionCard from './ChecklistSectionCard';
import { newItem, newSection, patchById, withoutId } from './checklistDraft';

const insertAfter = (list, id, entry) => {
	const at = list.findIndex(current => current.id === id) + 1;
	return [...list.slice(0, at), entry, ...list.slice(at)];
};

const ChecklistSectionList = props => {
	const { sections, event, groups, counts, savedItemIDs, onChange, onPickLink } = props;
	const [focusID, setFocusID] = useState(null);

	const changeItems = useCallback(
		(sectionID, change) =>
			onChange(current =>
				patchById(current, sectionID, section => ({ items: change(section.items) }))
			),
		[onChange]
	);
	const changeSection = useCallback(
		(sectionID, patch) => onChange(current => patchById(current, sectionID, patch)),
		[onChange]
	);
	const deleteSection = useCallback(
		sectionID => onChange(current => withoutId(current, sectionID)),
		[onChange]
	);
	const changeItem = useCallback(
		(sectionID, itemID, patch) => changeItems(sectionID, items => patchById(items, itemID, patch)),
		[changeItems]
	);
	const deleteItem = useCallback(
		(sectionID, itemID) => changeItems(sectionID, items => withoutId(items, itemID)),
		[changeItems]
	);
	const addItem = useCallback(
		(sectionID, afterID) => {
			const item = newItem();
			setFocusID(item.id);
			changeItems(sectionID, items =>
				afterID ? insertAfter(items, afterID, item) : [...items, item]
			);
		},
		[changeItems]
	);
	const pickLink = useCallback((sectionID, itemID) => onPickLink({ sectionID, itemID }), [
		onPickLink,
	]);

	const handleDragEnd = ({ type, source, destination }) => {
		if (!destination) return;
		if (type === 'section') {
			onChange(current => reorder(current, source.index, destination.index));
			return;
		}
		onChange(current =>
			patchById(current, source.droppableId, section => ({
				items: reorder(section.items, source.index, destination.index),
			}))
		);
	};

	return (
		<>
			<DragDropContext onDragEnd={handleDragEnd}>
				<Droppable droppableId="sections" type="section">
					{provided => (
						<div
							ref={provided.innerRef}
							{...provided.droppableProps}
							className="ChecklistEditor-sections"
						>
							{sections.map((section, index) => (
								<ChecklistSectionCard
									key={section.id}
									section={section}
									index={index}
									event={event}
									groups={groups}
									counts={counts}
									savedItemIDs={savedItemIDs}
									focusID={focusID}
									onChange={changeSection}
									onDelete={deleteSection}
									onPickLink={pickLink}
									onChangeItem={changeItem}
									onDeleteItem={deleteItem}
									onAddItem={addItem}
								/>
							))}
							{provided.placeholder}
						</div>
					)}
				</Droppable>
			</DragDropContext>
			<Button
				type="dashed"
				icon={<PlusOutlined />}
				onClick={() => onChange(current => [...current, newSection()])}
			>
				Add section
			</Button>
		</>
	);
};

export default ChecklistSectionList;
